import { Observable, catchError } from 'rxjs';
import { AbstractCrudApiService } from '@shared/abstracts/abstract-crud-api.service';
import { ProgramReferencesDto } from '@shared/dto';
import { urlJoin } from '@shared/utils/url.utils';

export abstract class AbstractProgramService<Model, CreateDto, UpdateDto> extends AbstractCrudApiService<Model, CreateDto, UpdateDto> {

  override get endpoint(): string {
    return `${this.api$.value}/:programId`;
  }

  protected constructor(
  ) {
    super();
    this.api$.next(urlJoin(this.env.get('api.base'), 'programs'));
    this.logger.debug(this.name, 'Endpoint', this.endpoint);
  }

  /**
   * True when a program with the given title already exists (backend unique-title check).
   *
   * Pass `programId` when RENAMING: that program is excluded from the check (otherwise it
   * collides with itself) and the title is compared in its owner's namespace rather than the
   * caller's — which is what the save will do. Same exclusion `@UniqueProgram` applies on
   * `PUT /programs/:id`, so this check and the resulting 400 cannot disagree.
   */
  validateTitle(title: string, programId?: number | null): Observable<boolean> {
    const params = new URLSearchParams({ title });
    if (programId != null) params.set('id', String(programId));
    return this.http.get<boolean>(`${this.url()}/validate-title?${params.toString()}`);
  }

  /**
   * Where this therapy is already in use: how many assignments/members it reaches, how many
   * are still live vs. frozen history, and whether other practitioners are involved. A pure
   * read — it changes nothing. Feeds the "already in use" banner on the edit page so an edit
   * is made with its downstream reach in view.
   */
  references(programId: number): Observable<ProgramReferencesDto> {
    return this.http.get<ProgramReferencesDto>(`${this.url({ programId })}/references`).pipe(
      catchError((error) => this.handleHttpErrorResponse(error)),
    );
  }
}
